import * as Y from 'yjs';
import * as awarenessProtocol from 'y-protocols/awareness';
import { IndexeddbPersistence } from 'y-indexeddb';
import { createUndoManager, bridgeUndoStateToRedux } from './undo';
import { yMeta, yElements, yLines } from './schema';
import { setupSocketSync } from './socket-sync';
import { setupSocketAwareness } from './socket-awareness';
import { setupAwarenessProducer } from './awareness-producer';
// store and setUndoState are imported lazily (inside
// getOrCreateDiagramDoc) to avoid pulling the full Redux/saga graph
// into lib/yjs unit tests that only touch createUndoManager. ESM
// live-binding semantics ensure the lazy require() always gets the
// initialised singleton.
/* eslint-disable */
const getStore         = () => require('../../redux/store').store;
const getSetUndoState  = () => require('../../redux/diagram/diagram.actions').setUndoState;
const getFocusActions  = () => require('../../redux/diagram/diagram.actions');
/* eslint-enable */

let currentDoc       = null;
let currentUndo      = null;
let currentDiagramId = null;
let currentPersistence = null;
let unbridge = null;
let unFocusCleanup = null;
let currentAwareness = null;
let socketSync       = null;
let socketAwareness  = null;
let awarenessProducer = null;

export const FORMAT_VERSION = 3;

// Clear stale focus IDs in Redux when their referenced Y.Doc entities are
// deleted. Without this, after deleteElements(doc, [id]) Redux's
// focusedElementsIds would still reference id, leading to crashes in
// hover/click handlers that look up the focused element by id.
function setupFocusCleanupObserver(doc) {
    const store = getStore();
    const { updateFocusedIds, updateFocusedInsideElement } = getFocusActions();

    const sync = () => {
        const state = store.getState().diagram;
        const elements = yElements(doc);
        const lines    = yLines(doc);

        // Remove deleted entities from focusedIds.
        const currentSelected = state.focusedIds || [];
        const validSelected = currentSelected.filter(
            id => elements.has(id) || lines.has(id)
        );
        if (validSelected.length !== currentSelected.length) {
            store.dispatch(updateFocusedIds(validSelected));
        }

        // Focused inside element (column inside a table).
        const inside = state.focusedInsideElement;
        if (inside?.elementId && !elements.has(inside.elementId)) {
            store.dispatch(updateFocusedInsideElement(null, null, null, null));
        } else if (inside?.elementId && inside?.insideElementId) {
            const yEl = elements.get(inside.elementId);
            const cols = yEl?.get('columns');
            if (!cols?.has(inside.insideElementId)) {
                store.dispatch(updateFocusedInsideElement(null, null, null, null));
            }
        }
    };

    yElements(doc).observe(sync);
    yLines(doc).observe(sync);

    return () => {
        yElements(doc).unobserve(sync);
        yLines(doc).unobserve(sync);
    };
}

function indexeddbStoreName(diagramId, userId) {
    // Namespaced by userId so cached diagrams don't leak across accounts on
    // shared/public devices, and so a sign-out cleanup can target one user's
    // stores by prefix without touching others.
    return `${userId}-diagram-${diagramId}`;
}

// y-indexeddb's _storeUpdate throws InvalidStateError if the IDB
// connection closes externally (versionchange from another tab, browser
// eviction). lib0's Observable.emit uses forEach, so the throw aborts
// iteration and the socket-sync observer registered later never
// broadcasts the update — silent divergence from collaborators. Catch
// keeps the observer chain intact; null persistence.db so future
// updates skip the IDB write cleanly.
function hardenPersistenceAgainstClose(persistence, doc) {
    const original = persistence._storeUpdate;
    const wrapped = (update, origin) => {
        try {
            original(update, origin);
        } catch (err) {
            if (err?.name === 'InvalidStateError') {
                persistence.db = null;
                return;
            }
            throw err;
        }
    };
    doc.off('update', original);
    doc.on('update', wrapped);
    persistence._storeUpdate = wrapped;
}

export function getOrCreateDiagramDoc(diagramId) {
    if (currentDoc && currentDiagramId === diagramId) return currentDoc;
    destroyDiagramDoc();
    currentDoc = new Y.Doc();
    currentDiagramId = diagramId;
    // L2: each diagram persists into its own IndexedDB store, namespaced by
    // userId. The persistence loads existing state asynchronously; consumers
    // should await whenDiagramDocSynced() before deciding whether to hydrate
    // from the API. If no user is authenticated (defensive — diagram routes
    // are gated by auth), skip persistence and run memory-only.
    const userId = getStore().getState()?.auth?.currentUser?.id;
    if (userId) {
        currentPersistence = new IndexeddbPersistence(indexeddbStoreName(diagramId, userId), currentDoc);
        hardenPersistenceAgainstClose(currentPersistence, currentDoc);
    }
    currentUndo = createUndoManager(currentDoc);
    unbridge = bridgeUndoStateToRedux(currentUndo, getStore(), getSetUndoState());
    unFocusCleanup = setupFocusCleanupObserver(currentDoc);
    currentAwareness = new awarenessProtocol.Awareness(currentDoc);
    socketSync       = setupSocketSync(currentDoc);
    socketAwareness  = setupSocketAwareness(currentAwareness);
    awarenessProducer = setupAwarenessProducer(currentAwareness, getStore());
    return currentDoc;
}

export function destroyDiagramDoc() {
    if (awarenessProducer) { awarenessProducer.destroy(); awarenessProducer = null; }
    if (socketAwareness) { socketAwareness.destroy(); socketAwareness = null; }
    if (socketSync)      { socketSync.destroy();      socketSync = null; }
    if (currentAwareness) { currentAwareness.destroy(); currentAwareness = null; }
    if (unFocusCleanup) {
        unFocusCleanup();
        unFocusCleanup = null;
    }
    if (unbridge) {
        unbridge();
        unbridge = null;
    }
    if (currentUndo) {
        currentUndo.destroy();
        currentUndo = null;
    }
    if (currentPersistence) {
        currentPersistence.destroy();
        currentPersistence = null;
    }
    if (currentDoc) {
        currentDoc.destroy();
        currentDoc = null;
    }
    currentDiagramId = null;
}

export function getDiagramDoc()       { return currentDoc; }
export function getCurrentDiagramId() { return currentDiagramId; }

// Delete every IndexedDB-backed diagram cache for the given user. Hooked
// into sign-out so a different user landing on a diagram URL on the same
// browser doesn't see the previous user's locally-cached state. Best-effort:
// `indexedDB.databases()` is unavailable on older Safari/Firefox; on those
// platforms the cleanup silently no-ops.
export async function clearLocalDiagramStores(userId) {
    if (!userId) return;
    if (typeof indexedDB === 'undefined') return;
    if (typeof indexedDB.databases !== 'function') return;
    const prefix = `${userId}-diagram-`;
    try {
        const dbs = await indexedDB.databases();
        await Promise.all(
            dbs
                .filter((db) => typeof db.name === 'string' && db.name.startsWith(prefix))
                .map((db) => new Promise((resolve) => {
                    const req = indexedDB.deleteDatabase(db.name);
                    req.onsuccess = () => resolve();
                    req.onerror = () => resolve();
                    req.onblocked = () => resolve();
                }))
        );
    } catch (_) {
        // best-effort; never block sign-out on cleanup failures
    }
}

// Resolves once IndexedDB has finished restoring persisted state into
// the current Y.Doc. Sagas await this before deciding whether to hydrate
// from the API: if the doc already has data, hydration would clobber the
// user's local edits.
export async function whenDiagramDocSynced() {
    if (!currentPersistence) return;
    await currentPersistence.whenSynced;
    const version = yMeta(currentDoc)?.get('formatVersion') ?? 0;
    if (version < FORMAT_VERSION && !isDiagramDocEmpty(currentDoc)) {
        currentDoc.transact(() => {
            yMeta(currentDoc).clear();
            yElements(currentDoc).clear();
            yLines(currentDoc).clear();
        });
    }
}

export function isDiagramDocEmpty(doc = currentDoc) {
    if (!doc) return true;
    return (
        yElements(doc).size === 0 &&
        yLines(doc).size === 0 &&
        yMeta(doc).size === 0
    );
}

// _setUndoManager is retained for backward-compat with createUndoManager,
// which (from Task 8) calls it after constructing the manager. This
// double-assignment to currentUndo is intentional and harmless: the
// getOrCreateDiagramDoc path explicitly stores the result, and undo.js's
// internal call to _setUndoManager just stores the same reference.
export function _setUndoManager(undo) { currentUndo = undo; }
export function getUndoManager() { return currentUndo; }

export function getDiagramAwareness() { return currentAwareness; }
export function getAwarenessProducer() { return awarenessProducer; }

// Trigger the y-protocols sync handshake. Called by the collab saga
// after a successful diagram:join ack and on every reconnect.
export function sendSyncStep1() {
    if (!socketSync) return;
    socketSync.sendSyncStep1();
}

// Apply the binary `members` snapshot delivered in the diagram:join
// ack. Called by the collab saga.
export function applyJoinMembers(snapshot) {
    if (!socketAwareness) return;
    socketAwareness.applyMembers(snapshot);
}
